import { getSsoUrl, getServiceId } from 'common/axios/getApiUrl';

// 토큰 관련 상수
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_INFO_KEY = 'userInfo';

/**
 * Access Token 가져오기
 * - localStorage 사용: 탭 간 공유, 브라우저 종료 후에도 유지
 */
export const getAccessToken = (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Refresh Token 가져오기
 */
export const getRefreshToken = (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * 토큰 저장하기
 * - 모든 프론트엔드 앱에서 동일한 위치(localStorage)에 저장
 */
export const setTokens = (accessToken: string, refreshToken?: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
};

/**
 * 토큰 삭제하기
 */
export const clearTokens = (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
};

/**
 * 로그인 상태 확인
 */
export const isLoggedIn = (): boolean => {
    const accessToken = getAccessToken();
    return !!accessToken;
};

/**
 * SSO 로그인 페이지로 리다이렉트
 * @param returnUrl 로그인 후 돌아갈 URL (현재 페이지 URL이 기본값)
 */
export const redirectToSsoLogin = (returnUrl?: string): void => {
    const ssoUrl = getSsoUrl();
    const serviceId = getServiceId();
    const currentUrl = returnUrl || window.location.href;

    // SSO 로그인 페이지로 이동
    // serviceId: 어떤 서비스에서 왔는지
    // returnUrl: 로그인 후 돌아갈 URL
    const loginUrl = `${ssoUrl}/sign-in?serviceId=${encodeURIComponent(serviceId)}&returnUrl=${encodeURIComponent(currentUrl)}`;

    window.location.href = loginUrl;
};

/**
 * SSO 회원가입 페이지로 리다이렉트
 * @param returnUrl 회원가입 후 돌아갈 URL
 */
export const redirectToSsoSignup = (returnUrl?: string): void => {
    const ssoUrl = getSsoUrl();
    const serviceId = getServiceId();
    const currentUrl = returnUrl || window.location.href;

    const signupUrl = `${ssoUrl}/sign-up?serviceId=${encodeURIComponent(serviceId)}&returnUrl=${encodeURIComponent(currentUrl)}`;

    window.location.href = signupUrl;
};

/**
 * SSO 콜백 처리 (URL에서 토큰 추출)
 * SSO 로그인 후 돌아올 때 URL에 토큰이 포함되어 있음
 * @returns 성공 여부
 */
export const handleSsoCallback = (): boolean => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('accessToken');
    const refreshToken = urlParams.get('refreshToken');

    if (accessToken) {
        setTokens(accessToken, refreshToken || undefined);

        // URL에서 토큰 파라미터 제거
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        return true;
    }

    return false;
};

/**
 * 인증이 필요한 페이지에서 사용할 함수
 * 로그인되어 있지 않으면 SSO로 리다이렉트
 * @param returnUrl 로그인 후 돌아갈 URL
 * @returns 로그인 상태
 */
export const requireAuth = (returnUrl?: string): boolean => {
    // SSO 콜백 처리 먼저 시도
    if (handleSsoCallback()) {
        return true;
    }

    // 로그인 상태 확인
    if (!isLoggedIn()) {
        redirectToSsoLogin(returnUrl);
        return false;
    }

    return true;
};
